<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToEllipsis'
});
</script>
<template>
	<span :key="langKey" :flex="flex ? flex : null" class="to-ellipsis" :class="setClass" :style="setStyle">
		<span v-if="data" ref="content" v-tip="tip && isFold && over ? data : ''" :style="setContentStyle" class="to-ellipsis-content" v-html="isFold ? iData : data"></span>
		<span v-else ref="content" :style="setContentStyle" class="to-ellipsis-content">
			<slot></slot>
		</span>
		<span v-if="over && data" class="to-ellipsis-fn">
			<template v-if="isFold && Number(count) > 1">...</template>
			<a v-if="isFold && unfoldable" href="javascript:void(0)" @click="toggle">{{ foldText || t('to.ellipsis.collapse') }}</a>
			<a v-if="!isFold && foldable" href="javascript:void(0)" @click="toggle">{{ unfoldText || t('to.ellipsis.expand') }}</a>
		</span>
	</span>
</template>

<script setup lang="ts">
import { ref, watch, computed, nextTick } from 'vue'
import { vTip } from '@touchvue/directives'
import { t, langKey } from '@touchvue/locale'



const props = defineProps({
	flex: Boolean,
	breakWord: Boolean,
	width: { type: [Number, String], default: '' },
	row: { type: [Number, String], default: 1 },
	count: { type: [Number, String], default: 0 },
	data: { type: [Number, String], default: '' },
	foldable: { type: Boolean, default: true },
	removeHtml: { type: Boolean, default: true },
	unfoldable: Boolean,
	clear: Boolean,
	tip: Boolean,
	foldText: { type: String, default: '' },
	unfoldText: { type: String, default: '' }
})

const content = ref<HTMLElement | null>(null)
const iData = ref('')
const isFold = ref(true)
const over = ref(false)

const type = computed(() => {
	if ((props.count ?? '') === '0' || props.count === 0 || (props.width ?? '') === '0' || props.width === 0 || (props.row ?? '') === '0' || props.row === 0) {
		return 'none'
	}
	if (Number(props.count) > 0) return 'count'
	if (props.width && props.width !== 'full' && !String(props.width).includes('%')) return 'width'
	if (Number(props.row) > 1) return 'row'
	return 'inline'
})

const setContentStyle = computed(() => {
	const obj: Record<string, any> = {}
	if (Number(props.row) > 1) obj.webkitLineClamp = props.row
	if (props.width && isFold.value) {
		if (!isNaN(Number(props.width))) obj.width = props.width + 'em'
		else if (props.width !== 'full' && !String(props.width).includes('%')) obj.width = props.width
	}
	return obj
})

const setStyle = computed(() => {
	if (props.width === 'full') return { width: '100%' }
	if (props.width) return { width: props.width }
	return {}
})

const setClass = computed(() => {
	const arr = []
	if (type.value === 'row' && isFold.value) arr.push('type-row')
	if (type.value === 'count') arr.push('type-count')
	if (type.value === 'width') arr.push('type-width')
	if (['width', 'inline', 'row'].includes(type.value) && !isFold.value) arr.push('is-expand')
	if (type.value === 'none') arr.push('is-expand')
	if (props.breakWord) arr.push('is-break')
	return arr
})

const getCount = (str: string) => {
	const countArr = (arr: RegExpMatchArray | null) => (arr ? arr.length : 0)
	const banjiao = countArr(str.match(/[\u0020-\u00ff]/g))
	const quanjiao = countArr(str.match(/[\uff00-\uffff]/g))
	const hanzi = countArr(str.match(/[\u4e00-\u9fa5]/g))
	return (banjiao + (quanjiao + hanzi) * 2) / 2
}

const setContent = () => {
	if (type.value === 'count') {
		let i = 1
		const plainText = props.data ? (props.removeHtml ? String(props.data).replace(/<[^>]*>/g, '') : String(props.data)) : ''
		let data = plainText.substr(0, i)
		while (getCount(data) < Number(props.count) && i < plainText.length) {
			i++
			data = plainText.substr(0, i)
		}
		over.value = plainText.length > i
		iData.value = data
	} else {
		iData.value = props.data ? (props.removeHtml ? String(props.data).replace(/<[^>]*>/g, '') : String(props.data)) : ''
	}
}

const setOver = () => {
	if (['inline', 'row', 'width'].includes(type.value)) {
		const el = document.createElement('div')
		el.style.position = 'absolute'
		if (type.value === 'inline') el.style.wordBreak = 'break-all'
		el.style.width = content.value?.offsetWidth + 'px'
		document.body.appendChild(el)

		const plainData = props.data ? (props.removeHtml ? String(props.data).replace(/<[^>]*>/g, '') : String(props.data)) : ''
		if (type.value === 'inline' || type.value === 'row') {
			el.innerHTML = type.value === 'row' ? '1<br />'.repeat(Number(props.row)) : '1'
			const baseHeight = el.offsetHeight
			el.innerHTML = plainData
			over.value = el.offsetHeight > baseHeight
		}
		if (type.value === 'width') {
			el.innerHTML = '1'
			const baseWidth = el.offsetWidth
			el.innerHTML = plainData
			el.style.width = 'auto'
			over.value = el.offsetWidth > baseWidth
		}
		document.body.removeChild(el)
	}
}

const toggle = () => {
	isFold.value = !isFold.value
}

const init = () => {
	setContent()
	nextTick(() => setTimeout(setOver, 100))
}

watch(() => props.data, init, { immediate: true })
watch(() => props.count, init)
watch(
	() => props.row,
	() => setTimeout(setOver, 100)
)
watch(
	() => props.width,
	() => setTimeout(setOver, 100)
)

defineExpose({
	toggle
})
</script>
